import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useMessageDetails } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { EmptyState, Icon, Spinner, Toast } from '@/ui/common';
import { MessageDetailsPanel } from '@/ui/profile';

export const Route = createFileRoute('/profile/messages/$messageId')({
  component: ProfileMessageDetailsPage,
});

function ProfileMessageDetailsPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { messageId } = Route.useParams();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const { details, isLoading, error } = useMessageDetails(
    messageId || null,
    Boolean(messageId),
  );

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <Link
        to="/profile/messages"
        className="inline-flex w-fit items-center gap-2 text-[13px] text-text-secondary hover:text-text-primary"
      >
        <Icon name="arrow-right" size={16} className="-scale-x-100" />
        {label('Messages', 'Messages')}
      </Link>

      <header className="flex flex-col gap-1 lg:gap-2">
        <h1 className="text-[18px] font-bold text-text-primary lg:text-[22px]">
          {label('Message', 'Message')}
        </h1>
        {details?.Title ? (
          <p className="text-[13px] text-text-secondary">{details.Title}</p>
        ) : null}
      </header>

      {error ? (
        <Toast tone="danger">
          {label('Could not load message', 'Could not load message')}
          {error.message ? `: ${error.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading message', 'Loading message')} />
        </div>
      ) : details ? (
        <MessageDetailsPanel details={details} />
      ) : !error ? (
        <EmptyState
          icon="messages"
          title={label('Message not found', 'Message not found')}
          description={label(
            'This message may have been removed.',
            'This message may have been removed.',
          )}
        />
      ) : null}
    </div>
  );
}
